import React, { createContext, useContext, useRef, useReducer } from "react";
import PropTypes from "prop-types";
import { OTP_LOGIN_ACTION, RESEND_OTP_TIME } from "../constants/authConstants";
import { otpSignIn } from "../layouts/authentication/_request";
import { useAuth } from "context";

const initialState = {
  otp: [],
  identifier: "",
  isOtpSent: false,
  timer: RESEND_OTP_TIME,
  isTimerRunning: true,
  isOtpCompleted: false,
  isLoading: false,
  message: null,
};

// Helper function to format phone numbers
const formatPhoneNumber = (identifier) => {
  // Check if it's a phone number (not an email)
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

  if (!isEmail) {
    // Format phone: remove all non-digit characters
    let digits = identifier.replace(/\D/g, "");

    // If it starts with country code 1 and has 11 digits, remove the 1
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.substring(1);
    }

    if (digits.length === 10) {
      return digits;
    }
  }

  return identifier;
};

const otpReducer = (state, action) => {
  switch (action.type) {
    case OTP_LOGIN_ACTION.SET_OTP:
      return {
        ...state,
        otp: action.payload,
        isOtpCompleted: action.payload.join("").length === state.otp.length,
      };
    case OTP_LOGIN_ACTION.SET_IDENTIFIER:
      return {
        ...state,
        identifier: action.payload.trim(),
      };
    case OTP_LOGIN_ACTION.DECREMENT_TIMER:
      return {
        ...state,
        timer: state.timer - 1,
        isTimerRunning: state.timer > 1,
      };
    case OTP_LOGIN_ACTION.BACK:
      return {
        ...state,
        isOtpSent: false,
        isTimerRunning: false,
        message: null,
      };
    case OTP_LOGIN_ACTION.OTP_SENT:
      return {
        ...state,
        otp: state.otp.fill(""),
        timer: RESEND_OTP_TIME,
        isTimerRunning: true,
        isOtpSent: true,
        isLoading: false,
        message: state.message,
      };
    case OTP_LOGIN_ACTION.TOGGLE_LOADING:
      return {
        ...state,
        isLoading: !state.isLoading,
      };
    case OTP_LOGIN_ACTION.SET_MESSAGE:
      return {
        ...state,
        message: action.payload,
      };
    case OTP_LOGIN_ACTION.MALFORMED_REQUEST:
      return {
        ...state,
        message: {
          message: "Malformed request!",
          type: "error",
        },
        isLoading: false,
      };
    case OTP_LOGIN_ACTION.RESET:
      return { ...initialState, otp: new Array(6).fill("") };
    default:
      return state;
  }
};

const OtpContext = createContext();

const useOtp = () => useContext(OtpContext);

const OtpProvider = ({ children, length }) => {
  const [state, dispatch] = useReducer(otpReducer, {
    ...initialState,
    otp: new Array(length).fill(""),
  });
  const inputRefs = useRef([]);
  const { showSnackbarWithMessage } = useAuth();

  const handleSendOtp = async (captchaToken, onCaptchaConsumed) => {
    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });

    // Format the identifier (if it's a phone number) before sending to the API
    const formattedIdentifier = formatPhoneNumber(state.identifier);

    const {
      data: { success, message },
      status,
    } = await otpSignIn({ identifier: formattedIdentifier, captchaToken });

    onCaptchaConsumed?.();

    if (status === 403) {
      dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
      showSnackbarWithMessage({ type: "error", message: "Verification failed, please try again." });
      return;
    }

    if (success) {
      dispatch({ type: OTP_LOGIN_ACTION.OTP_SENT });
    } else {
      dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
      showSnackbarWithMessage({ type: "error", message });
    }

    dispatch({
      type: OTP_LOGIN_ACTION.SET_MESSAGE,
      payload: {
        message: message,
        type: success ? "success" : "error",
      },
    });
  };

  return (
    <OtpContext.Provider
      value={{
        ...state,
        inputRefs,
        handleSendOtp,
        dispatch,
      }}
    >
      {children}
    </OtpContext.Provider>
  );
};

OtpProvider.propTypes = {
  length: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

export { OtpProvider, useOtp };
